import { NotificationType, useConversationImageUpload, useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import useToast from "@/hooks/useToast";

const logger = appLogger.withTag("useChatImageUpload");

interface IUseChatImageUploadArgs {
  fileInputRef: RefObject<HTMLInputElement | null>;
  onImageUploaded?: (key: string) => void;
  onImageCleared?: () => void;
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
  isEnabled?: boolean;
  resetSignal?: number;
}

const useChatImageUpload = ({
  onImageUploaded,
  fileInputRef,
  onImageCleared,
  onUploadingChange,
  disabled,
  isEnabled,
  resetSignal,
}: IUseChatImageUploadArgs) => {
  const [imageFileKey, setImageFileKey] = useState<string | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const [isImageCleared, setIsImageCleared] = useState(false);
  // Retains the local base64 of the attached image after upload completes. Used
  // to re-render the preview offline (e.g. a failed send caused by a dropped
  // connection) without depending on the CDN thumbnail, which cannot load while
  // the network is down.
  const refAttachedImageDataUrl = useRef<string | null>(null);

  const lastResetSignalRef = useRef(resetSignal);

  // Use business logic hook for API calls and state management
  const {
    onUserUploadImage,
    onUserDeleteImage,
    isUploading,
    errorMessage,
    resetImageUploadState: resetConversationImageUploadState,
    resetError,
  } = useConversationImageUpload();

  useEffect(() => {
    if (errorMessage) {
      displayToast({ message: errorMessage, type: NotificationType.error });
      resetError();
    }
  }, [errorMessage]);

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const clearHiddenInput = () => {
    const input = fileInputRef.current;
    if (input) {
      input.value = "";
    }
  };

  const onImageUpload = () => {
    const input = fileInputRef.current;
    if (!input) return;
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const dataURL = reader.result as string;
        setTempImageUrl(dataURL);
        refAttachedImageDataUrl.current = dataURL;
        const imageData = dataURL.split(",")[1];
        const contentType = dataURL.split(";")[0].split(":")[1];
        void saveUploadedFile(imageData, contentType);
      };
      reader.onerror = (error) => {
        logger.error("Error in onImageUpload", error);
        setTempImageUrl(null);
        displayToast({ message: t("chat:page.input.imageUpload.toast.error"), type: NotificationType.error });
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // Prefer the local base64 whenever it is present: while uploading (optimistic
  // preview) and after a restore (offline-safe preview). Falls back to the CDN
  // key for a normally attached image once the upload has finished.
  const displayImageKey = useMemo(() => {
    return tempImageUrl ? tempImageUrl : imageFileKey;
  }, [imageFileKey, tempImageUrl]);

  const processDroppedFile = useCallback(
    (files: File[]) => {
      if (disabled || !isEnabled || !files?.length) {
        return;
      }
      const file = files[0];

      const input = fileInputRef.current;
      if (!input) return;

      const originalClick = input.click.bind(input);
      (input as unknown as { click: () => void }).click = () => {};

      onImageUpload();
      setIsImageCleared(false);

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      const changeEvent = new Event("change", { bubbles: true });
      input.dispatchEvent(changeEvent);

      (input as unknown as { click: () => void }).click = originalClick;
    },
    [disabled, isEnabled, onImageUpload],
  );

  const handleClearImage = () => {
    void deleteImageUpload();
    setIsImageCleared(true);
    clearHiddenInput();
    onImageCleared?.();
    onUploadingChange?.(false);
  };

  const handleUploadClick = () => {
    if (disabled || !isEnabled) {
      return;
    }
    setIsImageCleared(false);
    onImageUpload();
  };

  const resetImageUploadState = () => {
    uploadAbortControllerRef.current?.abort();
    setImageFileKey(null);
    setImageId(null);
    setTempImageUrl(null);
    refAttachedImageDataUrl.current = null;
    uploadAbortControllerRef.current = null;
    resetConversationImageUploadState();
  };

  // Reads the retained base64 of the currently attached image. Called at send
  // time so the exact preview source can be restored if the send fails.
  const getAttachedImageDataUrl = useCallback(() => refAttachedImageDataUrl.current, []);

  // Re-populate a previously attached image (used to bring it back after a
  // failed send that optimistically cleared the composer). The preview is driven
  // by the retained base64 so it renders even while the connection is still
  // down; the real image key/id are kept for the retried send and later delete.
  const restoreImage = useCallback(
    ({ imageKey, imageId: restoredImageId, dataUrl }: { imageKey: string; imageId: string | null; dataUrl: string | null }) => {
      setImageFileKey(imageKey);
      setImageId(restoredImageId);
      setTempImageUrl(dataUrl);
      refAttachedImageDataUrl.current = dataUrl;
      setIsImageCleared(false);
    },
    [],
  );

  useEffect(() => {
    if (resetSignal === undefined) {
      return;
    }

    if (lastResetSignalRef.current !== resetSignal) {
      lastResetSignalRef.current = resetSignal;
      resetImageUploadState();
      setIsImageCleared(true);
      clearHiddenInput();
      onImageCleared?.();
      onUploadingChange?.(false);
    }
  }, [resetSignal, resetImageUploadState, onImageCleared, onUploadingChange]);

  const deleteImageUpload = async () => {
    const currentImageId = imageId;
    const currentImageKey = imageFileKey;

    resetImageUploadState();

    if (currentImageId && currentImageKey) {
      const result = await onUserDeleteImage({ imageId: currentImageId, imageKey: currentImageKey });
      if (result.error) {
        logger.warn("Failed to delete uploaded image:", result.error);
        displayToast({ message: result.error, type: NotificationType.error });
      }
    } else if (currentImageId) {
      logger.warn("Missing image key when attempting to delete image", { imageId: currentImageId });
    }
  };

  const saveUploadedFile = async (imageData: string, contentType: string) => {
    const controller = new AbortController();
    uploadAbortControllerRef.current?.abort();
    uploadAbortControllerRef.current = controller;

    const result = await onUserUploadImage({
      imageData,
      contentType,
      abortSignal: controller.signal,
    });

    if (uploadAbortControllerRef.current !== controller) {
      return;
    }

    if (result.error === "AbortError") {
      uploadAbortControllerRef.current = null;
      return;
    }

    if (result.error) {
      logger.error("Upload failed:", result.error);
      setTempImageUrl(null);
      uploadAbortControllerRef.current = null;
      displayToast({ message: result.error, type: NotificationType.error });
      return;
    }

    if (result.data) {
      setImageFileKey(result.data.imageKey);
      setImageId(result.data.imageId);
      onImageUploaded?.(result.data.imageKey);
      setIsImageCleared(false);

      // Clear temporary preview once real image is uploaded
      setTempImageUrl(null);
      uploadAbortControllerRef.current = null;
    }
  };

  return {
    imageId,
    isUploading,
    errorMessage,
    displayImageKey,
    isImageCleared,
    handleUploadClick,
    handleClearImage,
    processDroppedFile,
    restoreImage,
    getAttachedImageDataUrl,
  };
};

export default useChatImageUpload;
